import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Brain, BookOpen, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const AIToolsSection: React.FC = () => {
  const { t } = useTranslation('homepage');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const aiTools = [
    {
      id: 'zen-enlightenment',
      icon: Sparkles,
      title: t('aiZenEnlightenment'),
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      id: 'multidimensional-energy',
      icon: Brain,
      title: t('aiMultidimensionalEnergy'),
      gradient: 'from-teal-500 to-emerald-500',
    },
    {
      id: 'career-business-energy',
      icon: Sparkles,
      title: t('aiCareerBusinessEnergy'),
      gradient: 'from-violet-500 to-purple-500',
    },
  ];

  return (
    <section 
      ref={ref}
      className={`py-16 bg-amber-50 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      data-testid="ai-tools-section"
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-earth-900 mb-4">
            {t('aiTools')}
          </h2>
        </div>

        {/* Disclaimer Alert */}
        <div className="max-w-4xl mx-auto mb-10">
          <Alert className="bg-amber-50 border-amber-200">
            <AlertDescription className="text-sm text-earth-800">
              {t('aiToolsDisclaimer')}
            </AlertDescription>
          </Alert>
        </div>

        {/* AI Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {aiTools.map((tool, index) => {
            const IconComponent = tool.icon;
            return (
              <Card
                key={tool.id}
                className={`card-hover overflow-hidden text-center transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${tool.gradient} flex items-center justify-center mb-4 mx-auto`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-serif text-earth-900">
                    {tool.title}
                  </CardTitle>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Main CTA Button */}
        <div className="text-center">
          <Button
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg"
            asChild
          >
            <a
              href="https://resonow.qthos.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              {t('exploreAiTools')}
              <ExternalLink className="w-5 h-5" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AIToolsSection;
